import React from 'react';
import * as Form from 'react-hook-form';
import { Keyboard } from 'react-native';
import { t } from '@lingui/macro';
import { useNavigation } from '@react-navigation/native';

import SendForgetPasswordToken from '@src/api/SendForgetPasswordToken';
import {
  phoneNumberExtractor,
  phoneNumberValidator,
  phoneOrUsernameRegex,
  usernameValidator,
} from '@src/helpers';
import useSnackBar from '@src/hooks/useSnackBar';
import { i18n, i18yup } from '@src/i18n';

import { FormData, NavigationProp } from './types';

const useForm = () => {
  const [loading, setLoading] = React.useState(false);
  const navigation = useNavigation<NavigationProp>();
  const snackBar = useSnackBar();

  const form = Form.useForm<FormData>({
    validationSchema: i18yup.object<FormData>({
      identity: i18yup
        .string()
        .required()
        .matches(phoneOrUsernameRegex, i18n._(t`Must contain only digits`))
        .label(i18n._(t`Phone`)),
      region: i18yup
        .string()
        .required()
        .default('IR')
        .label(i18n._(t`Region`)),
    }),
  });

  const handleSubmit = React.useCallback(
    async ({ identity, region }: FormData) => {
      setLoading(true);
      try {
        Keyboard.dismiss();
        let response: Awaited<ReturnType<typeof SendForgetPasswordToken>>;

        const isPhone = phoneNumberValidator(identity);
        const isUsername = usernameValidator(identity);

        if (isPhone) {
          response = await SendForgetPasswordToken({
            identity: phoneNumberExtractor(identity, 'IR'),
            region,
            type: 'PHONE',
          });
        } else if (isUsername) {
          response = await SendForgetPasswordToken({
            identity,
            type: 'USERNAME',
          });
        } else {
          snackBar.show({ content: i18n._(t`Invalid Phone or Username`) });
          return;
        }

        if (response.data.result === 'SUCCESS') {
          const { preToken, ttl, identity: id } = response.data;
          const seconds = ttl;

          // TODO: replace it with more relative data from api
          if (id) {
            navigation.navigate('ResetPasswordOTP', {
              phone: id,
              preToken,
              ttl,
            });
          } else {
            snackBar.show({
              content: i18n._(t`Must wait ${seconds} to resend code`),
            });
            return;
          }
        }
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    },
    [navigation, snackBar]
  );

  return React.useMemo(
    () => ({
      form,
      handleSubmit: form.handleSubmit(handleSubmit),
      loading,
    }),
    [form, handleSubmit, loading]
  );
};

export default useForm;
